import { 
  signInWithPopup, 
  signInWithRedirect, 
  getRedirectResult,
  User,
  UserCredential 
} from 'firebase/auth';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';

import { auth, googleProvider, db } from '../../config/firebase';

/**
 * @service GoogleAuthService
 * @description Servicio para autenticación con Google SSO y gestión de usuarios
 */

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  createdAt: Date;
  lastLoginAt: Date;
  webIds: string[];
  isActive: boolean;
}

/**
 * Configurar Google Auth Provider con scopes específicos
 */
const configureGoogleProvider = () => {
  googleProvider.addScope('email');
  googleProvider.addScope('profile');
  googleProvider.setCustomParameters({
    prompt: 'select_account'
  });
};

/**
 * Iniciar sesión con Google usando popup
 */
export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    configureGoogleProvider();
    const result = await signInWithPopup(auth, googleProvider);
    
    // Crear o actualizar perfil de usuario
    await createOrUpdateUserProfile(result.user);
    
    return result;
  } catch (error: any) {
    console.error('Error en autenticación con Google:', error);
    
    // Manejar errores específicos
    if (error.code === 'auth/popup-blocked') {
      throw new Error('El popup fue bloqueado. Por favor, permite popups para este sitio.');
    } else if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Autenticación cancelada por el usuario.');
    } else if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Solicitud de popup cancelada.');
    }
    
    throw new Error('Error al iniciar sesión con Google. Inténtalo de nuevo.');
  }
};

/**
 * Iniciar sesión con Google usando redirect (para móviles)
 */
export const signInWithGoogleRedirect = async (): Promise<void> => {
  try {
    configureGoogleProvider();
    await signInWithRedirect(auth, googleProvider);
  } catch (error) {
    console.error('Error en redirect de Google:', error);
    throw new Error('Error al iniciar sesión con Google.');
  }
};

/**
 * Obtener resultado de redirect de Google
 */
export const getGoogleRedirectResult = async (): Promise<UserCredential | null> => {
  try {
    const result = await getRedirectResult(auth);
    
    if (result?.user) {
      await createOrUpdateUserProfile(result.user);
    }
    
    return result;
  } catch (error) {
    console.error('Error obteniendo resultado de redirect:', error);
    throw new Error('Error procesando autenticación con Google.');
  }
};

/**
 * Crear o actualizar perfil de usuario en Firestore
 */
export const createOrUpdateUserProfile = async (user: User): Promise<UserProfile> => {
  try {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    const now = new Date();
    
    if (userDoc.exists()) {
      // Usuario existente - actualizar última conexión
      const userData = userDoc.data();
      const updatedProfile: UserProfile = {
        ...userData as UserProfile,
        lastLoginAt: now,
        // Actualizar datos de Google si han cambiado
        displayName: user.displayName || userData.displayName,
        photoURL: user.photoURL || userData.photoURL,
      };
      
      await setDoc(userRef, {
        ...updatedProfile,
        lastLoginAt: Timestamp.fromDate(now),
      }, { merge: true });
      
      return updatedProfile;
    } else {
      // Nuevo usuario - crear perfil completo
      const newProfile: UserProfile = {
        uid: user.uid,
        email: user.email!,
        displayName: user.displayName || 'Usuario',
        photoURL: user.photoURL || undefined,
        createdAt: now,
        lastLoginAt: now,
        webIds: [],
        isActive: true,
      };
      
      await setDoc(userRef, {
        ...newProfile,
        createdAt: Timestamp.fromDate(now),
        lastLoginAt: Timestamp.fromDate(now),
      });
      
      return newProfile;
    }
  } catch (error) {
    console.error('Error creando/actualizando perfil de usuario:', error);
    throw new Error('Error al procesar datos del usuario.');
  }
};

/**
 * Obtener perfil de usuario desde Firestore
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
      } as UserProfile;
    }
    
    return null;
  } catch (error) {
    console.error('Error obteniendo perfil de usuario:', error);
    return null;
  }
};

/**
 * Asociar WebID con usuario
 */
export const associateWebIDWithUser = async (
  userId: string, 
  webId: string
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentWebIds = userData.webIds || [];
      
      // Agregar WebID si no existe ya
      if (!currentWebIds.includes(webId)) {
        await setDoc(userRef, {
          webIds: [...currentWebIds, webId],
        }, { merge: true });
      }
    }
  } catch (error) {
    console.error('Error asociando WebID con usuario:', error);
    throw new Error('Error al asociar perfil de emergencia con usuario.');
  }
};

/**
 * Verificar si el usuario puede activar un WebID
 */
export const canUserActivateWebID = async (
  userId: string, 
  webId: string
): Promise<boolean> => {
  try {
    // Verificar que el WebID esté habilitado
    const webIdRef = doc(db, 'webids', webId);
    const webIdDoc = await getDoc(webIdRef);
    
    if (!webIdDoc.exists()) {
      return false;
    }
    
    const webIdData = webIdDoc.data();
    
    // Verificar que esté habilitado y no activado por otro usuario
    return webIdData.isEnabled === true && 
           (webIdData.userId === null || webIdData.userId === userId);
  } catch (error) {
    console.error('Error verificando permisos de activación:', error);
    return false;
  }
};

/**
 * Detectar si es dispositivo móvil para usar redirect en lugar de popup
 */
export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Función principal para autenticación con Google
 * Usa popup en desktop y redirect en móvil
 */
export const authenticateWithGoogle = async (): Promise<UserCredential> => {
  if (isMobileDevice()) {
    await signInWithGoogleRedirect();
    // En móvil, el resultado se obtiene después del redirect
    throw new Error('REDIRECT_IN_PROGRESS');
  } else {
    return await signInWithGoogle();
  }
};
